import { useEffect, useRef } from "react";
import {
  ROUNDS,
  randomDelayMs,
  useReflexStore,
  type Phase,
} from "../store.js";
import { haptic } from "@karrtopelka/game-kit";

/**
 * The play surface. One big pointer target covering every phase. Zero instructions
 * beyond the in-state label (DoD #1: plays in <30s, no instructions needed).
 *
 * The waiting→ready flip timer lives here (component), not the store: scheduled in an
 * effect keyed on phase+completed, cleared on unmount / early tap / phase change.
 */
const SURFACE: Record<Phase, string> = {
  idle: "bg-surface",
  waiting: "bg-[#3a0d1c]", // deep red-pink: "don't tap yet"
  ready: "bg-accent", // soft pink flash: TAP
  tooSoon: "bg-[#2a0a12]",
  done: "bg-surface",
};

export function Board() {
  const phase = useReflexStore((s) => s.phase);
  const completed = useReflexStore((s) => s.completed);
  const lastMs = useReflexStore((s) => s.lastMs);
  const arm = useReflexStore((s) => s.arm);
  const flip = useReflexStore((s) => s.flip);
  const tap = useReflexStore((s) => s.tap);
  const retry = useReflexStore((s) => s.retry);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const el = useRef<HTMLButtonElement | null>(null);

  // Schedule the green flip whenever we enter a waiting window.
  useEffect(() => {
    if (phase !== "waiting") return;
    const id = setTimeout(() => flip(performance.now()), randomDelayMs());
    timer.current = id;
    return () => {
      clearTimeout(id);
      timer.current = null;
    };
  }, [phase, completed, flip]);

  const fire = () => {
    switch (phase) {
      case "idle":
        haptic.tap();
        arm();
        break;
      case "waiting":
        // early tap: kill the pending flip, mark tooSoon
        if (timer.current) clearTimeout(timer.current);
        haptic.tooSoon();
        tap(performance.now());
        break;
      case "ready": {
        // Record before reading the store: tap() advances completed; the buzz reflects
        // whether this was the final round (done) vs a mid-run round.
        const wasLast = completed + 1 >= ROUNDS;
        tap(performance.now());
        if (wasLast) {
          if (useReflexStore.getState().isNewBest) haptic.newBest();
          else haptic.done();
        } else {
          haptic.round();
        }
        break;
      }
      case "tooSoon":
        retry();
        break;
      case "done":
        break; // summary handles next actions
    }
  };

  // Desktop keyboard support: space/enter act as a tap. Timing is captured at keydown
  // inside fire() via performance.now(), same as pointerdown — no keyup lag. We route
  // through a ref so the mount-once listener always sees the latest phase closure.
  // Native button activation is a non-issue: we never listen for click.
  const fireRef = useRef<() => void>(fire);
  useEffect(() => {
    fireRef.current = fire;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.code !== "Enter") return;
      if (e.repeat) return;
      const t = e.target;
      if (t instanceof HTMLElement) {
        // Don't hijack typing or keyboard activation of links/other buttons
        // (leaderboard range tabs, footer). The board button itself still counts.
        if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "A")
          return;
        if (t.tagName === "BUTTON" && t !== el.current) return;
      }
      e.preventDefault();
      fireRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <button
      ref={el}
      type="button"
      onPointerDown={fire}
      className={`relative flex h-[60dvh] max-h-[640px] min-h-[280px] w-full max-w-2xl touch-none select-none flex-col items-center justify-center rounded-lg border border-border text-center transition-colors duration-100 active:scale-[0.995] hover:border-accent/50 lg:h-[64vh] lg:max-h-[720px] lg:max-w-none ${
        SURFACE[phase]
      } ${phase === "tooSoon" ? "animate-shake" : ""} ${
        phase === "idle" ? "animate-neon-pulse" : ""
      }`}
      aria-label="reaction test surface"
    >
      <BoardLabel phase={phase} completed={completed} lastMs={lastMs} />
      <RoundDots completed={completed} />
    </button>
  );
}

function BoardLabel({
  phase,
  completed,
  lastMs,
}: {
  phase: Phase;
  completed: number;
  lastMs: number | null;
}) {
  switch (phase) {
    case "idle":
      return (
        <>
          <span className="text-3xl font-bold text-text lg:text-5xl">
            tap to start
          </span>
          <span className="mt-2 text-sm text-text-muted lg:text-base">
            {ROUNDS} rounds · fastest tap wins
            <span className="hidden lg:inline"> · space works too</span>
          </span>
        </>
      );
    case "waiting":
      return (
        <>
          <span className="animate-breathe text-3xl font-bold text-text lg:text-5xl">
            wait for pink…
          </span>
          {lastMs !== null && (
            <span className="mt-2 font-display text-sm text-text-muted lg:text-base">
              last: {lastMs}ms
            </span>
          )}
        </>
      );
    case "ready":
      return (
        <span className="animate-pop text-7xl font-extrabold text-bg lg:text-9xl">
          TAP
        </span>
      );
    case "tooSoon":
      return (
        <>
          <span className="text-3xl font-bold text-accent lg:text-5xl">
            too soon
          </span>
          <span className="mt-2 text-sm text-text-muted lg:text-base">
            tap to retry round
          </span>
        </>
      );
    case "done":
      return null;
  }
}

/** Cabinet lamps: one bulb per counted round, lit with a soft pink glow. */
function RoundDots({ completed }: { completed: number }) {
  return (
    <div className="absolute bottom-6 flex gap-2.5 lg:gap-3">
      {Array.from({ length: ROUNDS }, (_, i) => (
        <span
          key={i}
          className={`h-2.5 w-2.5 rounded-full transition-all duration-200 lg:h-3 lg:w-3 ${
            i < completed
              ? "bg-accent shadow-[0_0_10px_rgba(255,111,165,0.7)]"
              : "bg-border"
          }`}
        />
      ))}
    </div>
  );
}
